import React, { useState, useEffect } from 'react';
import { Input } from '../ui/Input';
import { PhoneInput } from '../ui/PhoneInput';
import { Button } from '../ui/Button';
import { apiList } from '../../api/apiClient';

interface UserFormProps {
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  presetRole?: string;
  presetIntegratorId?: number | string;
  presetCustomerId?: number | string;
  presetPropertyId?: number | string;
  initialData?: any;
}

const ROLES = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'integrator', label: 'Dealer' },
  { value: 'dealer_admin', label: 'Dealer Admin' },
  { value: 'customer', label: 'Customer' },
  { value: 'customer_admin', label: 'Customer Admin' },
  { value: 'property_manager', label: 'Property Manager' },
];

const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export const UserForm: React.FC<UserFormProps> = ({
  onSubmit,
  onCancel,
  presetRole,
  presetIntegratorId,
  presetCustomerId,
  presetPropertyId,
  initialData,
}) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    role: presetRole || 'customer',
    status: 'active',
    integrator_id: presetIntegratorId,
    customer_id: presetCustomerId,
    property_ids: presetPropertyId ? [presetPropertyId] : [],
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        password: '',
        role: initialData.role || presetRole || 'customer',
        status: initialData.status || 'active',
        integrator_id: initialData.integrator_id || presetIntegratorId,
        customer_id: initialData.customer_id || presetCustomerId,
        property_ids: initialData.property_ids || (presetPropertyId ? [presetPropertyId] : []),
      });
    }
  }, [initialData, presetRole, presetIntegratorId, presetCustomerId, presetPropertyId]);

  const set = (key: string, val: any) => {
    setFormData((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!initialData && !formData.password.trim()) newErrors.password = 'Password is required for new users';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload: any = {
        name: `${formData.first_name} ${formData.last_name}`.trim(),
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone || undefined,
        role: formData.role,
        status: formData.status,
      };

      // Add password for new users
      if (!initialData && formData.password) {
        payload.password = formData.password;
      }

      // Add relationship IDs
      if (formData.integrator_id) payload.integrator_id = formData.integrator_id;
      if (formData.customer_id) payload.customer_id = formData.customer_id;
      if (formData.property_ids && formData.property_ids.length > 0) {
        payload.property_ids = formData.property_ids;
      }

      await onSubmit(payload);
    } catch (error: any) {
      setErrors({ submit: error?.message || 'Failed to save user' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {errors.submit && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {errors.submit}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="First Name *"
          value={formData.first_name}
          onChange={(e) => set('first_name', e.target.value)}
          error={errors.first_name}
          required
        />
        <Input
          label="Last Name *"
          value={formData.last_name}
          onChange={(e) => set('last_name', e.target.value)}
          error={errors.last_name}
          required
        />
      </div>

      <Input
        label="Email *"
        type="email"
        value={formData.email}
        onChange={(e) => set('email', e.target.value)}
        error={errors.email}
        required
      />

      <PhoneInput
        label="Phone"
        value={formData.phone || ''}
        onChange={(v) => set('phone', v)}
      />

      {!initialData && (
        <Input
          label="Password *"
          type="password"
          value={formData.password}
          onChange={(e) => set('password', e.target.value)}
          error={errors.password}
          required
          placeholder="Minimum 8 characters"
        />
      )}

      {!presetRole && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Role *
          </label>
          <select
            value={formData.role}
            onChange={(e) => set('role', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            required
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <ScopePicker
        role={formData.role}
        customerId={formData.customer_id}
        integratorId={formData.integrator_id}
        onCustomer={(v) => set('customer_id', v)}
        onIntegrator={(v) => set('integrator_id', v)}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Status *
        </label>
        <select
          value={formData.status}
          onChange={(e) => set('status', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          required
        >
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex gap-3 justify-end mt-6 pt-4 border-t">
        <Button variant="secondary" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={loading}>
          {initialData ? 'Update' : 'Create'} User
        </Button>
      </div>
    </form>
  );
};

interface ScopePickerProps {
  role: string;
  customerId: any;
  integratorId: any;
  onCustomer: (v: any) => void;
  onIntegrator: (v: any) => void;
}

const ScopePicker: React.FC<ScopePickerProps> = ({ role, customerId, integratorId, onCustomer, onIntegrator }) => {
  const needsCustomer = role === 'customer' || role === 'customer_admin';
  const needsDealer = role === 'integrator' || role === 'dealer_admin';
  const [customers, setCustomers] = useState<any[]>([]);
  const [dealers, setDealers] = useState<any[]>([]);
  const [loadedKind, setLoadedKind] = useState<string>('');

  useEffect(() => {
    if (needsCustomer && loadedKind !== 'customers') {
      apiList('customers').then((rows) => {
        setCustomers(Array.isArray(rows) ? rows : []);
        setLoadedKind('customers');
      }).catch(() => setCustomers([]));
    }
    if (needsDealer && loadedKind !== 'dealers') {
      apiList('dealers').then((rows) => {
        setDealers(Array.isArray(rows) ? rows : []);
        setLoadedKind('dealers');
      }).catch(() => setDealers([]));
    }
  }, [needsCustomer, needsDealer, loadedKind]);

  if (!needsCustomer && !needsDealer) return null;

  if (needsCustomer) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Customer *</label>
        <select
          value={String(customerId || '')}
          onChange={(e) => onCustomer(e.target.value || null)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          required
        >
          <option value="">— Select customer —</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.company_name || c.name || `Customer #${c.id}`}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Dealer *</label>
      <select
        value={String(integratorId || '')}
        onChange={(e) => onIntegrator(e.target.value || null)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        required
      >
        <option value="">— Select dealer —</option>
        {dealers.map((d) => (
          <option key={d.id} value={d.id}>
            {d.company_name || d.name || `Dealer #${d.id}`}
          </option>
        ))}
      </select>
    </div>
  );
};
